import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { Observable, of, combineLatest, forkJoin } from 'rxjs';

import { catchError, filter, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import { InstallationDetailsActions, InstallationDetailsStore } from '../installation-details';
import { InstallationFeaturesActions } from './installation-features.actions';

import * as Api from '@vi-iot/client';
import {
  APP_INSIGHTS_FEATURES,
  AppInsightsMonitoringService,
  LogLevelEnum,
} from '../../shared/services/app-insights';
import {
  FetchFeaturesForInstallationGatewaysOutcome,
  FetchFeaturesForInstallationGatewaysOutcomeResolved,
  roomsErrorsTranslations,
} from './installation-features.models';
import { LiveUpdatesService } from '../../shared/live-updates/live-updates.service';
import { isReady } from '../_common/is-ready';
import { UpdateMessageService } from '../_services/update-message.service';
import { UIService } from '../../shared/services';
import { InstallationFeaturesStore } from './installation-features.store';
import { OperationalDataApiPicker } from '../../../projects/client/apis/operational-data/operational-data.api-picker';
import { DevicesStore } from '../devices/devices.store';
import { RichDevice } from '../devices/devices.state';
import { ActivatedRoute } from '@angular/router';
import { Feature } from '@vi-iot/client';
import { environment } from 'src/environments/environment';
import { ViToastService } from '@vi-iot/ui/index';
import { Helpers } from '@store';

@Injectable()
export class InstallationFeaturesEffects {
  constructor(
    private api: Api.OperationalDataApi,
    private client: Api.ClientService,
    private actions$: Actions,
    private installationDetailsStore: InstallationDetailsStore,
    private installationFeaturesStore: InstallationFeaturesStore,
    private operationalDataApiPicker: OperationalDataApiPicker,
    private appInsights: AppInsightsMonitoringService,
    private liveUpdatesService: LiveUpdatesService,
    private updateMessageService: UpdateMessageService,
    private uiService: UIService,
    private translate: TranslateService,
    private devicesStore: DevicesStore,
    private activatedRoute: ActivatedRoute,
    private gatewayUpdateApi: Api.GatewayUpdateApi,
    private toast: ViToastService
  ) {}

  getMockedFeature$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(InstallationFeaturesActions.getMockedFeature),
      concatLatestFrom(() => this.activatedRoute.queryParams),
      mergeMap(([action, params]) =>
        this.client
          .rawGet<Api.FeatureDeprecated<{}, {}>>(
            `mockedAssets/${params.gatewaySerial}/${action.path}`
          )
          .pipe(
            map((data) => {
              const viessmannDomain = 'viessmann.com';
              const indexOfViessmannDomain = data.uri.indexOf(viessmannDomain);
              data.uri =
                environment.headerModuleApiUrl +
                data.uri.substring(
                  indexOfViessmannDomain + viessmannDomain.length,
                  data.uri.length
                );

              return InstallationFeaturesActions.upsertManyFeatures({
                features: [data as Feature<unknown, unknown>],
              });
            }),
            catchError(() => of(null))
          )
      )
    );
  });

  triggerNewWayToListAllGatewayFeatures$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InstallationDetailsActions.ReadInstallationDetailsSuccess),
      map(() => InstallationFeaturesActions.fetchFeaturesForInstallationGateways.attempt())
    );
  });

  fetchFeaturesForAllGateways$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InstallationFeaturesActions.fetchFeaturesForInstallationGateways.attempt),
      mergeMap(() =>
        combineLatest([
          this.installationDetailsStore.getInstallationId$.pipe(take(1)),
          this.installationDetailsStore.installationGateways$.pipe(take(1)),
        ])
      ),
      switchMap(([installationId, gateways]) => {
        if (!gateways || gateways.some((gateway) => !gateway.serial)) {
          return of(
            InstallationFeaturesActions.fetchFeaturesForInstallationGateways.aborted({
              installationId,
            })
          );
        }
        const gatewaySerials = gateways.map((gateway) => gateway.serial);

        let requests: FetchFeaturesForInstallationGatewaysOutcome = {};
        gatewaySerials.forEach((selectedGatewaySerial: string) => {
          requests[selectedGatewaySerial] = this.operationalDataApiPicker
            .instance()
            .listAllGatewayFeatures(installationId, selectedGatewaySerial)
            .pipe(
              map((payload) => payload.data),
              map((featuresArray) => featuresArray.filter((feature) => isReady(feature))),
              catchError((error) => {
                this.appInsights.logEvent(
                  APP_INSIGHTS_FEATURES.GET_ALL_GATEWAY_FEATURES_ERROR,
                  LogLevelEnum.ERROR,
                  { error }
                );

                return of(null);
              })
            );
        });

        return forkJoin(requests).pipe(
          map((fetchFeaturesOutcome: FetchFeaturesForInstallationGatewaysOutcomeResolved) => {
            const fetchFeaturesFailed = Object.values(fetchFeaturesOutcome).every(
              (subscriptionResult) => subscriptionResult === null
            );

            if (fetchFeaturesFailed) {
              return InstallationFeaturesActions.fetchFeaturesForInstallationGateways.error();
            } else {
              return InstallationFeaturesActions.fetchFeaturesForInstallationGateways.success({
                gatewaySerials,
                features: Object.values(fetchFeaturesOutcome)
                  .filter((outcome) => outcome !== null)
                  .flatMap((outcome) => outcome),
              });
            }
          })
        );
      })
    );
  });

  triggerLiveUpdateOfFeatures$: Observable<void> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(InstallationFeaturesActions.fetchFeaturesForInstallationGateways.success),
        concatLatestFrom(() => this.devicesStore.selectDeviceEntities$),
        map(([action, deviceEntities]) => {
          // update gateway features
          this.liveUpdatesService.subscribeGatewayFeatures(action.gatewaySerials);
          // we still need to iterate over each device in each gateway to get LU for it
          const devicesArray: RichDevice[] = Object.values(deviceEntities);
          this.liveUpdatesService.subscribeDeviceFeatures(devicesArray);
        })
      );
    },
    { dispatch: false }
  );

  listAllGatewayFeatures$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InstallationFeaturesActions.fetchFeatures.attempt),
      mergeMap(() =>
        combineLatest([
          this.installationDetailsStore.getInstallationId$.pipe(take(1)),
          this.installationDetailsStore.getSelectedGateway$.pipe(
            filter((selectedGateway) => !!selectedGateway),
            take(1)
          ),
        ])
      ),
      switchMap(([installationId, selectedGateway]) =>
        this.api.listAllGatewayFeatures(installationId, selectedGateway?.serial).pipe(
          map((payload) => payload.data),
          map((featuresArray) => featuresArray.filter((feature) => isReady(feature))),
          map((features) => InstallationFeaturesActions.fetchFeatures.success({ features })),
          catchError((error) => {
            this.appInsights.logEvent(
              APP_INSIGHTS_FEATURES.GET_ALL_GATEWAY_FEATURES_ERROR,
              LogLevelEnum.ERROR,
              { error }
            );

            return of(InstallationFeaturesActions.fetchFeatures.error(error));
          })
        )
      )
    );
  });

  triggerUpdateOfFeature$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InstallationFeaturesActions.updateFeature.attempt),
      concatLatestFrom((action) =>
        this.installationFeaturesStore.selectInstallationDeviceFeature(
          action.featureName,
          action.deviceId,
          action.gatewaySerial
        )
      ),
      map(([action, feature]) => ({
        ...action,
        feature,
      })),
      map((action) => Helpers.mapToPayload(action)),
      tap(() => this.uiService.blockUI()),
      switchMap((action) =>
        this.client.rawPost(action.uri, action.payload).pipe(
          tap(() => this.uiService.unblockUI()),
          tap(() => {
            if (action.config && action.config.hideSuccessSnackbar) {
              return;
            }
            this.updateMessageService.success(
              action.config.successMessage,
              action.featureName,
              action.commandName
            );
          }),
          map(() =>
            InstallationFeaturesActions.updateFeature.success({
              featureName: action.featureName,
              config: action.config,
            })
          ),
          catchError((error) => {
            this.uiService.unblockUI();

            const errorSlug = this.getErrorSlug(error.extendedPayload?.details);
            const errorMessage = errorSlug
              ? this.translate.instant(errorSlug)
              : action.config.errorMessage;

            this.updateMessageService.error(errorMessage, action.featureName, action.commandName);

            return of(
              InstallationFeaturesActions.updateFeature.error({
                featureName: action.featureName,
                property: action.propertyName,
                error,
              })
            );
          })
        )
      )
    );
  });

  triggerReplaceGateway$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(InstallationFeaturesActions.updateReplaceGateway.attempt),
      concatLatestFrom(() => this.installationFeaturesStore.replaceGatewayFormData$),
      tap(() => this.uiService.blockUI()),
      switchMap(([_action, replaceGatewayFormData]) =>
        this.gatewayUpdateApi.replaceGateway(replaceGatewayFormData).pipe(
          map(() => {
            this.uiService.unblockUI();
            this.toast.info({
              message: this.translate.instant(
                '+REPLACE_GATEWAY_MODAL.REPLACE_GATEWAY_SUCCESSFULLY'
              ),
            });

            return InstallationFeaturesActions.updateReplaceGateway.success();
          }),
          catchError((error: Api.ViError) => {
            this.uiService.unblockUI();
            switch (error.statusCode) {
              case 404:
                this.toast.error({
                  message: this.translate.instant('+REPLACE_GATEWAY_MODAL.ERROR.GATEWAY_NOT_FOUND'),
                });
                break;
              case 405:
                this.toast.error({
                  message: this.translate.instant('+REPLACE_GATEWAY_MODAL.ERROR.WRONG_PASSWORD'),
                });
                break;
              default:
                break;
            }

            return of(InstallationFeaturesActions.updateReplaceGateway.error({ error }));
          })
        )
      )
    );
  });

  triggerSmartNotesFeatures$: Observable<void> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(InstallationDetailsActions.ReadInstallationDetailsSuccess),
        map((actions) => {
          this.liveUpdatesService.subscribeInstallationSmartNotes(
            actions.payload.installation.id.toString()
          );
        })
      );
    },
    { dispatch: false }
  );

  private getErrorSlug(errorMessage: string): string {
    return roomsErrorsTranslations[errorMessage];
  }
}
